import { AppComponent, Browser, BrowserEvent, BrowserState } from "@reporting/rp-app-host";
import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Accordion, AccordionItemState, ComponentId, ComponentStore, dom, DropdownMenu, MenuEvent } from "@omegagrid/core";
import { style } from "./sidebar.style";
import { ThreadBrowserAdapter, FileBrowserAdapter } from "../adapters";
import { msg } from "@omegagrid/localize";

@customElement('rp-mf-etl-sidebar')
export class Sidebar extends AppComponent {

	static styles = [style];

	@query('rp-browser')
	browser: Browser;

	@query('#menu')
	dropdownMenu: DropdownMenu;

	@property({type: String})
	currentComponentId = 'filters'; //'contexts';

	@query('og-accordion')
	accordion: Accordion;

	browserComponentStore = new ComponentStore<Browser>((id: ComponentId) => {
		const browser = dom.createElement<Browser>('rp-browser');
		browser.addEventListener('browser.select', this._onSelect);
		browser.addEventListener('browser.state', this._onChangeState);
		switch (id) {
			case 'threads':
				browser.adapter = new ThreadBrowserAdapter();
				browser.state = this.state?.threadBrowser as BrowserState;
				break;
			case 'files':
				browser.adapter = new FileBrowserAdapter();
				browser.state = this.state?.fileBrowser as BrowserState;
				break;
		}
		return browser;
	});

	layout() {
		this.accordion?.layout();
	}

	_onSelect = (e: BrowserEvent) => {
		this.host.open(e.node.key, {title: e.node.value});
	}

	_onChangeState = () => {
		this.dispatchStateEvent();
	}

	/** Applies the persisted accordion state (collapsed + size) onto freshly built items, matched by id. */
	_restoreAccordionItems = <T extends {id?: ComponentId}>(items: T[]): T[] => {
		const saved = this.state?.accordion as AccordionItemState[] | undefined;
		if (!saved) return items;
		return items.map(item => {
			const s = saved.find(x => x.id === item.id);
			return s ? {...item, collapsed: s.collapsed, size: s.size} : item;
		});
	}

	getState() {
		return {
			name: 'sidebar',
			fileBrowser: null as BrowserState,
			threadBrowser: this.browserComponentStore.items.get('threads')?.getState(),
			accordion: this.accordion?.getItemsState(),
		}
	}

	refresh = async (id: ComponentId) => {
		(await this.browserComponentStore.get(id))?.refresh();
	};

	toggleCollapse = async (id: ComponentId) => {
		(await this.browserComponentStore.get(id))?.tree?.toggleTreeCollapse();
	};

	_onMenuSelect = (e: MenuEvent) => {
		const chunks = e.item.key.toString().split('.');
		const action = chunks[0];
		const target = chunks[1];
		const targetPlural = target + (target.endsWith('s') ? '' : 's');
		switch (action) {
			case 'merge':
				this.host.open(this.createComponentId('merge', target), { title: msg(`Merge ${targetPlural}`) });
				return;
			case 'new':
				this.host.openModalSidebar({ id: `etl-${target}Form`, caption: msg(`New ${target}`) });
				return;
			case 'grid':
				const title = targetPlural.at(0)?.toUpperCase() + targetPlural.slice(1);
				this.host.open(this.createComponentId(targetPlural), { title: msg(title) });
				return;
		}
	}

	renderThreadHeader = () => html`
		<div class="header" style="display: flex; align-items: center; gap: 5px;">
			<div style="flex: 1">${msg('Threads')}</div>
			<div style="flex: 0; display: flex">
				<og-button
					icon="search"
					title="${msg('Search')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.host.openCommands('ETL');
					}}">
				</og-button>

				<og-button
					icon="arrows-rotate"
					title="${msg('Refresh')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.refresh('threads');
					}}">
				</og-button>

				<og-button
					icon="square-plus"
					title="${msg('Collapse/expand tree')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.toggleCollapse('threads')
					}}">
				</og-button>

				<og-dropdown-menu
					noCaret
					dynamicWidth
					style="min-width: 25px;"
					icon="bars"
					color="transparent"
					@click="${(e: PointerEvent) => e.stopImmediatePropagation()}"
					@menu.select="${this._onMenuSelect}"
					.items="${[
						{key: 'new.thread', value: msg('New thread'), icon: 'plus'},
						{key: 'merge.thread', value: msg('Merge threads'), icon: 'code-merge'},
						{key: 'grid.thread', value: msg('Grid editor'), icon: 'table'},
					]}">
				</og-dropdown-menu>
			</div>
		</div>
	`;

	renderFileHeader = () => html`
		<div class="header" style="display: flex; align-items: center; gap: 5px;">
			<div style="flex: 1">${msg('Files')}</div>
			<div style="flex: 0; display: flex">
				<og-button
					icon="search"
					title="${msg('Search')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.host.openCommands('ETL');
					}}">
				</og-button>

				<og-button
					icon="arrows-rotate"
					title="${msg('Refresh')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.refresh('files');
					}}">
				</og-button>

				<og-button
					icon="square-plus"
					title="${msg('Collapse/expand tree')}"
					color="transparent"
					style="padding: 0 5px;"
					@click="${(e: PointerEvent) => {
						e.stopImmediatePropagation();
						this.toggleCollapse('files')
					}}">
				</og-button>

				<og-dropdown-menu
					noCaret
					dynamicWidth
					style="min-width: 25px;"
					icon="bars"
					color="transparent"
					@click="${(e: PointerEvent) => e.stopImmediatePropagation()}"
					@menu.select="${this._onMenuSelect}"
					.items="${[
						{key: 'new.file', value: msg('New file'), icon: 'plus'},
						{key: 'merge.file', value: msg('Merge files'), icon: 'code-merge'},
						{key: 'grid.file', value: msg('Grid editor'), icon: 'table'},
					]}">
				</og-dropdown-menu>
			</div>
		</div>
	`;

	render = () => html`
		<og-accordion
			.createComponent="${(id: ComponentId) => this.browserComponentStore.get(id)}"
			.items="${this._restoreAccordionItems([
				{id: 'threads', title: this.renderThreadHeader(), icon: 'database'},
				{id: 'files', title: this.renderFileHeader() , icon: 'files'},
			])}">
		</og-accordion>
	`;

}
